import { makeStyles } from '@material-ui/core/styles';
import { EntityStackFilter, CustomFilters } from '../CustomFilters';
import { useEntityList } from '@backstage/plugin-catalog-react';
import {
  Select,
  MenuItem,
  FormControl,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@material-ui/core';

/** @public */
export type CatalogReactEntityStackPickerClassKey = 'input';

const useStyles = makeStyles(
  theme => ({
    input: {
      backgroundColor: theme.palette.background.paper,
    },
  }),
  { name: 'CatalogReactEntityStackPicker' },
);

/** @public */
export const EntityStackPicker = () => {
  const classes = useStyles();

  const {
    filters: { stack },
    updateFilters,
  } = useEntityList<CustomFilters>();

  // All stack options - always show every allowed value regardless of current
  // data. Values mirror docs/schema.md (metadata.labels: heroku.com/stack).
  const stackOptions = [
    { value: 'platform', label: 'Platform' },
    { value: 'kernel', label: 'Kernel' },
    { value: 'dogwood-kernel', label: 'Dogwood Kernel' },
    { value: 'fir-kernel', label: 'Fir Kernel' },
    { value: 'ec2-non-kernel', label: 'EC2 Non-Kernel' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'other', label: 'Other' },
    { value: 'none', label: 'No Stack' },
    { value: '__missing__', label: 'No Stack Assigned' },
  ];

  function onChange(event: React.ChangeEvent<{ value: unknown }>) {
    const values = event.target.value as string[];
    updateFilters({
      stack: values.length ? new EntityStackFilter(values) : undefined,
    });
  }

  const selectedValues = stack?.values || [];

  return (
    <div>
      <label
        id="stack-picker-label"
        style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}
        htmlFor="stack-picker"
      >
        Stack
      </label>
      <FormControl
        variant="outlined"
        size="small"
        fullWidth
        className={classes.input}
      >
        <Select
          multiple
          id="stack-picker"
          labelId="stack-picker-label"
          value={selectedValues}
          onChange={onChange}
          input={<OutlinedInput />}
          renderValue={selected => (
            <Box display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
              {(selected as string[]).map(value => {
                const option = stackOptions.find(opt => opt.value === value);
                return (
                  <Chip
                    key={value}
                    label={option?.label || value}
                    size="small"
                    onDelete={() => {
                      const newValues = selectedValues.filter(v => v !== value);
                      updateFilters({
                        stack: newValues.length
                          ? new EntityStackFilter(newValues)
                          : undefined,
                      });
                    }}
                  />
                );
              })}
            </Box>
          )}
        >
          {stackOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={selectedValues.indexOf(option.value) > -1} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
